<template>
  <div
    class="task-card p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg hover:-translate-y-1"
    :class="cardClass"
    @click="$emit('start', task)"
  >
    <!-- Stars -->
    <div class="flex gap-0.5 mb-2">
      <MTIcon
        v-for="i in 3"
        :key="i"
        name="star"
        :size="14"
        :class="i <= task.stars ? 'text-yellow-400' : 'text-gray-300'"
      />
    </div>

    <!-- Title -->
    <h4 class="font-semibold text-sm mb-1 line-clamp-2 text-[var(--mt-text)]">{{ task.title }}</h4>

    <!-- Subtitle -->
    <p class="text-xs text-[var(--mt-text-muted)] mb-3 line-clamp-1">{{ task.subtitle }}</p>

    <!-- Footer: XP + Button -->
    <div class="flex items-center justify-between">
      <span class="text-xs font-bold text-purple-600">+{{ task.xp }} XP</span>
      <span
        class="text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1"
        :class="buttonClass"
      >
        <MTIcon name="play" :size="12" />
        Start
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import MTIcon from "@/components/ui/MTIcon.vue";
import { usePreferencesStore } from "@/stores/preferencesStore";

const preferencesStore = usePreferencesStore();
const props = defineProps({
  task: {
    type: Object,
    required: true
    // Expected shape:
    // {
    //   type: string,
    //   title: string,
    //   subtitle: string,
    //   xp: number,
    //   stars: number (1-3),
    //   route: string
    // }
  }
});

defineEmits(["start"]);

const isDark = computed(() => preferencesStore.homeTheme === 'space-mission');

const cardClass = computed(() => {
  const light = {
    continue: "border-blue-200 bg-blue-50",
    weakTopic: "border-orange-200 bg-orange-50",
    newLesson: "border-green-200 bg-green-50",
    revision: "border-purple-200 bg-purple-50",
    quickQuiz: "border-cyan-200 bg-cyan-50",
    challenge: "border-red-200 bg-red-50",
    diagnostic: "border-amber-200 bg-amber-50"
  };
  const dark = {
    continue: "border-blue-700 bg-blue-950/40",
    weakTopic: "border-orange-700 bg-orange-950/40",
    newLesson: "border-green-700 bg-green-950/40",
    revision: "border-purple-700 bg-purple-950/40",
    quickQuiz: "border-cyan-700 bg-cyan-950/40",
    challenge: "border-red-700 bg-red-950/40",
    diagnostic: "border-amber-700 bg-amber-950/40"
  };
  const classes = isDark.value ? dark : light;
  return classes[props.task.type] || (isDark.value ? "border-slate-600 bg-slate-800" : "border-gray-200 bg-white");
});

const buttonClass = computed(() => {
  const classes = {
    continue: "bg-blue-500 text-white",
    weakTopic: "bg-orange-500 text-white",
    newLesson: "bg-green-500 text-white",
    revision: "bg-purple-500 text-white",
    quickQuiz: "bg-cyan-500 text-white",
    challenge: "bg-red-500 text-white",
    diagnostic: "bg-amber-500 text-white"
  };
  return classes[props.task.type] || "bg-gray-500 text-white";
});
</script>
